import React, { useMemo, useState } from 'react';
import { Symptom } from '../lib/types';
import { TRACKABLE_SYMPTOMS } from '../lib/symptomCategories';
import { computeLineOfBestFit, getSymptomScore } from '../lib/correlationEngine';
import { triggerHaptic } from '../lib/haptics';
import { ChevronRight, MoreHorizontal, Activity } from 'lucide-react';

interface SymptomSummaryCardProps {
  symptomId: string;
  symptoms: Symptom[];
  timeframeDays?: number;
  timeframeLabel?: string;
  onOpen: () => void;
  onSelectSymptom?: (symptomId: string) => void;
}

export const SymptomSummaryCard: React.FC<SymptomSummaryCardProps> = ({
  symptomId,
  symptoms,
  timeframeDays = 7,
  timeframeLabel = 'Last 7 Days',
  onOpen,
  onSelectSymptom,
}) => {
  const [showPicker, setShowPicker] = useState(false);

  const meta = useMemo(() => {
    return (
      TRACKABLE_SYMPTOMS.find((s) => s.id === symptomId) || {
        id: symptomId,
        name: symptomId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        shortName: symptomId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        unit: '/10',
        description: '',
      }
    );
  }, [symptomId]);

  // Compute 7-day data points on 0-10 scale
  // Compute timeframe data points on 0-10 scale
  const { points, avgDisplay, bestFitLine, scatterDots } = useMemo(() => {
    const now = Date.now();
    const startMs = now - timeframeDays * 24 * 60 * 60 * 1000;

    const filtered = symptoms
      .filter((s) => {
        const t = new Date(s.timestamp_iso).getTime();
        return !isNaN(t) && t >= startMs && t <= now;
      })
      .sort((a, b) => new Date(a.timestamp_iso).getTime() - new Date(b.timestamp_iso).getTime());

    if (filtered.length === 0) {
      return {
        points: [],
        avgDisplay: '0.0 / 10',
        bestFitLine: null,
        scatterDots: [],
      };
    }

    const pts = filtered.map((s) => {
      const val = getSymptomScore(s, symptomId);
      const timeMs = new Date(s.timestamp_iso).getTime();
      const progress = now > startMs ? Math.max(0, Math.min(1, (timeMs - startMs) / (now - startMs))) : 0.5;
      return {
        timeMs,
        progress,
        val,
      };
    });

    const values = pts.map((p) => p.val);
    const avg = (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);

    const fit = computeLineOfBestFit(pts.map((p) => ({ progress: p.progress, val: p.val })));

    // Sparkline coordinate mapping (Width 110, Height 26 on 0-10 scale)
    const x1 = 4;
    const y1 = 23 - (fit.scoreStart / 10) * 19;
    const x2 = 106;
    const y2 = 23 - (fit.scoreEnd / 10) * 19;

    const scatter = pts.map((p) => {
      const x = 4 + p.progress * 102;
      const y = 23 - (p.val / 10) * 19;
      return { x, y, val: p.val };
    });

    return {
      points: pts,
      avgDisplay: `${avg} / 10`,
      bestFitLine: { x1, y1, x2, y2 },
      scatterDots: scatter,
    };
  }, [symptoms, symptomId, timeframeDays]);

  return (
    <div className="relative aspect-square bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 rounded-3xl p-4 shadow-xs hover:shadow-sm transition-all flex flex-col justify-between">
      {/* Top Header */}
      <div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-black text-slate-950 dark:text-white truncate">
            {meta.shortName}
          </span>

          {onSelectSymptom && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                triggerHaptic('light');
                setShowPicker(!showPicker);
              }}
              className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-all cursor-pointer"
              title="Change Tracked Symptom"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 mt-0.5">
          {timeframeLabel}
        </div>
      </div>

      {/* Middle Sparkline: Line of Best Fit + Scatter Points (No point-to-point lines) */}
      <div
        className="my-auto cursor-pointer"
        onClick={() => {
          triggerHaptic('light');
          onOpen();
        }}
      >
        {bestFitLine ? (
          <svg viewBox="0 0 110 26" className="w-full h-8 overflow-visible">
            {/* Straight Line of Best Fit */}
            <line
              x1={bestFitLine.x1}
              y1={bestFitLine.y1}
              x2={bestFitLine.x2}
              y2={bestFitLine.y2}
              stroke="#3B82F6"
              strokeWidth={2}
              strokeLinecap="round"
            />
            {/* Discrete scatter dots (including 0) */}
            {scatterDots.map((d, i) => (
              <circle
                key={i}
                cx={d.x}
                cy={d.y}
                r={d.val > 0 ? 2 : 1.5}
                fill={d.val >= 7 ? '#EF4444' : d.val >= 4 ? '#F59E0B' : d.val > 0 ? '#3B82F6' : '#10B981'}
                opacity={d.val > 0 ? 0.9 : 0.55}
              />
            ))}
          </svg>
        ) : (
          <div className="h-8 flex items-center justify-center text-[10px] text-slate-300 dark:text-zinc-600 space-x-1">
            <Activity className="w-3.5 h-3.5 opacity-40" />
            <span>0.0 Baseline</span>
          </div>
        )}
      </div>

      {/* Bottom Row */}
      <div
        className="flex items-center justify-between cursor-pointer pt-1.5 border-t border-slate-100 dark:border-zinc-800/60"
        onClick={() => {
          triggerHaptic('light');
          onOpen();
        }}
      >
        <span className="text-xs font-black text-slate-900 dark:text-white">
          {avgDisplay}
        </span>
        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
      </div>

      {/* Popover Symptom Selector */}
      {showPicker && onSelectSymptom && (
        <div className="absolute top-10 right-2 z-40 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 rounded-2xl p-1.5 shadow-xl w-44 max-h-52 overflow-y-auto animate-scale-in">
          <div className="text-[9px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 px-2 py-1">
            Select Symptom
          </div>
          {TRACKABLE_SYMPTOMS.map((sym) => (
            <button
              key={sym.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                triggerHaptic('light');
                onSelectSymptom(sym.id);
                setShowPicker(false);
              }}
              className={`w-full text-left px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                symptomId === sym.id
                  ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 font-black'
                  : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800'
              }`}
            >
              <span className="truncate">{sym.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
